import { useEffect, useState } from "react";

/**
 * Returns `value` but only after it has stayed unchanged for `delayMs`.
 * Useful for coalescing rapid changes before reacting to them — e.g. a
 * type-ahead autocomplete dropdown, or debouncing an external filter that can
 * toggle several times in quick succession.
 *
 * NOTE: the widget Search panels deliberately do NOT use this to fire searches
 * on keystroke — searches run only on an explicit submit (Enter / Search
 * button) to avoid a request per character. See `src/ui-components/search-bar.tsx`.
 *
 * Example (autocomplete):
 *   const [query, setQuery] = useState("");
 *   const debounced = useDebouncedValue(query, 300);
 *   const { data } = useQuery({ queryKey: ["suggest", debounced], enabled: debounced.length >= 2 });
 */
export function useDebouncedValue<T>(value: T, delayMs: number = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export default useDebouncedValue;
